import { useLang } from "../../context/LanguageContext";
import { images, unitImages } from "../../lib/api";
import { Link } from "react-router-dom";
import { Home, Trees, Flame, Sun } from "lucide-react";

export default function Property() {
  const { t, lang } = useLang();
  const loc = lang === "de" ? DE : EN;
  const gal = unitImages.zirben.gallery;

  return (
    <section id="property" data-testid="property-section" className="section-pad bg-[hsl(var(--cream))]">
      <div className="container-x">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-14">
          <Home className="w-5 h-5 text-[hsl(var(--accent))]" />
          <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]">
            {loc.sectionLabel}
          </span>
          <div className="flex-1 h-px bg-[hsl(var(--foreground))]/10" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: text content */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline mb-6">{t.intro.eyebrow}</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))] whitespace-pre-line">
              {t.intro.title}
            </h2>
            <p className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-[hsl(var(--foreground))]/88">
              {t.intro.body}
            </p>

            {/* Features list */}
            <ul className="mt-8 space-y-2.5 text-sm text-[hsl(var(--foreground))]/85 max-w-lg">
              {loc.features.map((f, i) => (
                <li key={i} data-testid={`zirben-feature-${i}`} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-6 border-t border-[hsl(var(--foreground))]/15 pt-6">
              {t.intro.stats.map((s, i) => (
                <div key={i}>
                  <div className="font-serif text-3xl md:text-4xl text-[hsl(var(--accent))]">{s.value}</div>
                  <div className="mt-1 text-[11px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/88">{s.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book?unit=zirben" data-testid="zirben-cta-book" className="btn-primary">
                {loc.cta} <span aria-hidden>→</span>
              </Link>
              <a
                href="https://wa.me/436641004293?text=Hallo!%20Ich%20interessiere%20mich%20für%20das%20Zirben%20Chalet."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="zirben-cta-whatsapp"
                className="btn-ghost !border-[hsl(var(--foreground))]/40"
              >
                {loc.ask}
              </a>
            </div>
          </div>

          {/* Right: images */}
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="aspect-[4/5] overflow-hidden" style={{ borderRadius: "4px" }}>
              <img src={images.heroWinter} alt="Zirben Chalet im Winter" className="w-full h-full object-cover gallery-img" />
            </div>
            <div className="absolute -bottom-10 -left-6 md:-left-12 w-44 md:w-56 aspect-[3/4] overflow-hidden border-8 border-[hsl(var(--cream))] hidden md:block" style={{ borderRadius: "4px" }}>
              <img src={images.living} alt="Zirben Chalet Wohnzimmer" className="w-full h-full object-cover gallery-img" />
            </div>
            {/* Price badge */}
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2.5 shadow-lg" style={{ borderRadius: "4px" }}>
              <div className="text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/60 font-medium">{lang === "de" ? "Ab" : "From"}</div>
              <div className="font-serif text-2xl text-[hsl(var(--accent))]">€300<span className="text-sm font-sans text-[hsl(var(--foreground))]/50">/{lang === "de" ? "Nacht" : "night"}</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const DE = {
  sectionLabel: "Unterkunft 1 von 2",
  features: [
    "4 Schlafzimmer aus warmem Zirbenholz",
    "2 Badezimmer mit Dusche",
    "Offener Wohnbereich mit weißem Kachelofen",
    "Voll ausgestattete Küche zum Verweilen",
    "Sonnige Terrasse mit Bergblick · privater Garten",
    "Kostenfreies WLAN · Privatparkplatz",
  ],
  cta: "Zirben Chalet buchen",
  ask: "Anfrage per WhatsApp",
};

const EN = {
  sectionLabel: "Property 1 of 2",
  features: [
    "4 bedrooms in warm stone pine wood",
    "2 bathrooms with shower",
    "Open living area with white tiled stove",
    "Fully equipped kitchen made for lingering",
    "Sunny terrace with mountain views · private garden",
    "Free Wi-Fi · private parking",
  ],
  cta: "Book Zirben Chalet",
  ask: "Ask on WhatsApp",
};
